import * as React from 'react';
import { Store } from '../../../stores';
import { inject, observer } from 'mobx-react';
import * as styles from './SlideEditPanel.css';
import { IAPanel } from '../../../../panels/Panel';
import { IATextField } from '../../../../textField/TextField';
import { LocalizationService } from '../../../../../services/LocalizationService';
import { IAButton } from '../../../../button/Button';
import { IADateAndTimePicker } from '../../../../dateAndTimePicker/DateAndTimePicker';
import { IAOptionSelector } from '../../../../optionSelector/OptionSelector';
import { IAToggle } from '../../../../toggle/Toggle';
import { Slide } from '../../../../player/slide/Slide';
import PlayService from '../../../../../services/PlayService';
import { IPlaylist, IPlayRssTickerWidget, IPlaySlideExtended, IPlaySlideLayout, IPlayWidget, IPlayWidgetExtended, IPlayWidgetType } from '../../../../../interfaces/IPlay';
import { IASpinner } from '../../../../spinner/Spinner';
import { Helper } from '../../../../../Helper';
import { PlayHelper } from '../../../PlayHelper';
import { WidgetEditPanel } from '../../widgets/widgetEditPanel/WidgetEditPanel';
import { WidgetPopup } from '../../widgetPopup/WidgetPopup';
import { IACheckbox } from '../../../../checkbox/Checkbox';
import { Popup } from '../../../reusableComponents/popup/Popup';
import { RssTicker } from '../../../../player/rssTicker/RssTicker';
import { Footer } from '../../../../player/footer/Footer';
import { IMAGE_CONFETTI_TEMPLATE_LOCALIZATION_KEYS } from '../../../../navigator/Constants';
import Snowfall from 'react-snowfall';
import Confetti from 'confetti-react';
import { createImageConfettiDrawShape, linearTween, preloadConfettiImages } from '../../../../global/ImageConfettiHelper';
import { IAMessageBar } from '../../../../messageBar/MessageBar';

export interface IProps {
  slideId: string;
  store?: Store;
  showPlaylistSelector?: boolean;
  isOpen: boolean;
  delete?: () => void;
  close: () => void;
  onSave: () => void;
}

export interface IState {
  isSaving: boolean;
  selectedWidgetIndex: number;
  selectedWidgetType: IPlayWidgetType;
  selectedRssTickerPosition: string;
  selectedRssTicker: IPlayRssTickerWidget;
  availableWidgets: IPlayWidget[];
  showRssTickerPopup: boolean;
  playlists: IPlaylist[];
  playlistsCount: number;
  isLoadingMore: boolean;
  showConfirmClosePopup: boolean;
  showDeleteButtonPopup?: boolean;
  imageConfettiReadyKey?: string;
}

@inject('store')
@observer
export class SlideEditPanel extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly perPage: number = 5;
  private imageConfettiRequestKey?: string;
  private isUnmounted = false;
  private readonly resolveConfettiImageSrc = (imageUrl: string): string => {
    if (imageUrl.indexOf("https://intraactivestorage.blob.core.windows.net/cdn/confetti/") === 0 || PlayHelper.excludeImageFromApi(this.props.store.environment, imageUrl)) {
      return imageUrl;
    }
    return PlayHelper.getImageFromApi(this.props.store.environment, this.props.store.tenantId, this.props.store.profile?.id, "64x64", false, imageUrl);
  };

  constructor(props: IProps) {
    super(props);
    this.state = {
      isSaving: false,
      selectedWidgetIndex: undefined,
      selectedWidgetType: undefined,
      selectedRssTickerPosition: undefined,
      selectedRssTicker: undefined,
      availableWidgets: undefined,
      showRssTickerPopup: false,
      isLoadingMore: false,
      playlists: [],
      playlistsCount: undefined,
      showConfirmClosePopup: false
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
    this.ensureImageConfettiPreloaded();
  }

  public componentDidUpdate(): void {
    this.ensureImageConfettiPreloaded();
  }

  public componentWillUnmount(): void {
    this.isUnmounted = true;
  }

  private getImageConfettiUrls(): string[] {
    return (this.props.store.slideToEdit?.imageConfettiUrls || []).filter((imageUrl) => !!imageUrl);
  }

  private ensureImageConfettiPreloaded(): void {
    const imageUrls = this.getImageConfettiUrls();
    const requestKey = this.props.store.slideToEdit?.showImageConfetti && imageUrls.length > 0 ? JSON.stringify(imageUrls) : undefined;
    if (requestKey === this.imageConfettiRequestKey) {
      return;
    }
    this.imageConfettiRequestKey = requestKey;
    this.setState({ imageConfettiReadyKey: undefined });
    if (requestKey) {
      preloadConfettiImages(imageUrls, this.resolveConfettiImageSrc).then(() => {
        if (!this.isUnmounted && this.imageConfettiRequestKey === requestKey) {
          this.setState({ imageConfettiReadyKey: requestKey });
        }
      });
    }
  }

  public componentWillReceiveProps(props: IProps): void {
    this.props.showPlaylistSelector && this.state.playlists?.length < this.perPage && props.isOpen && this.loadPlaylists();
    if (props.slideId !== this.props.slideId && props.slideId) {
      PlayService.getSlide(this.props.store.environment, this.props.store.token, this.props.store.tenantId, this.props.store.profile?.id, props.slideId).then((slide: IPlaySlideExtended) => {
        this.props.store.slideToEdit = { ...slide, playlistsToAddTo: [], playlistsToRemoveFrom: [] };
        this.props.store.slideToEditBeforeChanges = JSON.stringify(this.props.store.slideToEdit);
        if (slide?.rssTicker?.rssTicker) {
          this.setState({ selectedRssTicker: slide?.rssTicker?.rssTicker });
        }
        this.forceUpdate();
      }).catch(e => {
        this.props.store.workingOnIt = false;
        this.props.store.generalErrorPopup = {
          technicalDetails: e
        };
      });
    }
  }

  private getWidgetsOfType(type: IPlayWidgetType): void {
    let availableWidgets: IPlayWidget[];
    this.setState({ availableWidgets });
    if (type) {
      PlayService.getWidgets(this.props.store.environment, this.props.store.token, this.props.store.tenantId, this.props.store.profile?.id, type).then(widgets => {
        availableWidgets = [];
        widgets.value.forEach((widget: IPlayWidget) => {
          availableWidgets.push(widget);
        });
        this.setState({ availableWidgets });
      }).catch(e => {
        this.props.store.workingOnIt = false;
        this.props.store.generalErrorPopup = {
          technicalDetails: e
        };
      });
    }
  }

  private loadPlaylists() {
    this.setState({ isLoadingMore: true })
    const currentPage = Math.ceil(this.state.playlists?.length / this.perPage) || 0;
    PlayService.getPlaylists(this.props.store.environment, this.props.store.token, this.props.store.tenantId, this.props.store.profile?.id,
      {
        skip: currentPage * this.perPage,
        take: this.perPage
      })
      .then(result => {
        if (!this.state.playlistsCount) {
          this.setState({ playlistsCount: result.count })
        }
        this.setState({ playlists: [...this.state.playlists, ...result.value], isLoadingMore: false })
      }).catch(e => {
        this.props.store.workingOnIt = false;
        this.props.store.generalErrorPopup = {
          technicalDetails: e
        };
      });
  }

  public render(): JSX.Element {
    const imageConfettiTemplates = PlayHelper.getImageConfettiTemplates(this.props.store.profile);
    let previewWidth = this.props.store.windowWidth - 480;
    let previewHeight = (previewWidth / 16) * 9;

    // resize if the window height is smaller than the preview height
    if (previewHeight > window.innerHeight - 84) {
      previewHeight = window.innerHeight - 84 - 80;
      previewWidth = (previewHeight / 9) * 16;
    }
    if (this.props.store.isMobile) {
      previewWidth = window.innerWidth - 20;
      previewHeight = (previewWidth / 16) * 9;
    }
    // Portrait mode
    if (this.props.store.slideToEdit?.layout === "portraitOneRow" || this.props.store.slideToEdit?.layout === "portraitTwoRows") {
      previewHeight = window.innerHeight - 84 - 80;
      previewWidth = (previewHeight / 16) * 9;
    }
    return (
      <IAPanel
        // Stays open (not pushed off-screen) while a nested WidgetEditPanel is active, otherwise
        // that position:fixed child escapes this now off-screen panel visually but not for hit-testing.
        open={this.props.store.slideToEdit != undefined || this.props.slideId != undefined}
        transition={"slide"}
        showNavigationBar={true}
        marginTop={0}
        isMobile={this.props.store.isMobile}
        loading={this.state.isSaving}
        width={this.props.store.windowWidth}
        darkMode={this.props.store.darkMode}
        dataAutomationIdPrefix="slide-edit-panel"
        panelId="slide-edit-panel"
        navigationsBarContent={
          <>
            <IAButton
              label={this.props.store.slideToEdit?.id ? this.localizationService.strings.PlayAdmin_Update?.toUpperCase() : this.localizationService.strings.PlayAdmin_Save?.toUpperCase()}
              buttonColor={this.props.store.highlightColor}
              disbaled={(!this.props.store.slideToEdit?.title)}
              darkMode={this.props.store.darkMode}
              onClick={() => {
                this.setState({ playlists: [], playlistsCount: undefined })
                this.props.onSave();
              }}
              borderRadius={5}
              showSpinner={this.props.store.workingOnIt}
              style={{
                float: "right",
                marginTop: 7,
                marginRight: 10
              }}
            />
            {this.props.store.slideToEdit?.id &&
              <IAButton
                label={this.localizationService.strings.PlayAdmin_Delete?.toUpperCase()}
                buttonColor='rgb(223, 79, 79)'
                darkMode={this.props.store.darkMode}
                onClick={() => {
                  this.setState({ showDeleteButtonPopup: true });
                }}
                borderRadius={5}
                showSpinner={this.props.store.workingOnIt}
                style={{
                  float: "right",
                  marginTop: 7,
                  marginRight: 10
                }}
              />
            }
          </>
        }

        close={() => {
          if (this.props.store.slideToEditBeforeChanges === JSON.stringify(this.props.store.slideToEdit)) {
            this.setState({ selectedRssTicker: undefined, playlists: [], playlistsCount: undefined, showConfirmClosePopup: false })
            this.props.close();
          } else {
            this.setState({ showConfirmClosePopup: true });
          }
        }}
        isInTeams={this.props.store.isTeams}
      >
        {this.state.showDeleteButtonPopup &&
          <Popup
            headline={this.localizationService.strings.PlayAdmin_DeleteSlide}
            close={() => this.setState({ showDeleteButtonPopup: false })}
            darkMode={this.props.store.darkMode}
            content={
              <>
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_DeletePopup_Yes}
                  buttonColor={this.props.store.highlightColor}
                  darkMode={this.props.store.darkMode}
                  onClick={() => {
                    this.props.delete();
                    this.setState({ showDeleteButtonPopup: false });
                  }}
                  borderRadius={5}
                  style={{
                    float: "left",
                  }}
                />
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_DeletePopup_No}
                  textColor={this.props.store.highlightColor}
                  darkMode={this.props.store.darkMode}
                  onClick={() => this.setState({ showDeleteButtonPopup: false })}
                  borderRadius={5}
                  style={{
                    float: "left",
                    marginLeft: 30
                  }}
                />
              </>
            }
          />
        }
        {this.state.showConfirmClosePopup &&
          <Popup
            headline={this.localizationService.strings.Messages_CancelPopup_Headline}
            close={() => this.setState({ showConfirmClosePopup: false })}
            darkMode={this.props.store.darkMode}
            content={
              <>
                <IAButton
                  buttonColor={this.props.store.highlightColor}
                  darkMode={this.props.store.darkMode}
                  label={this.localizationService.strings.PlayAdmin_DeletePopup_Yes?.toUpperCase()}
                  onClick={() => {
                    this.setState({ selectedRssTicker: undefined, playlists: [], playlistsCount: undefined, showConfirmClosePopup: false })
                    this.props.close();
                  }}
                  borderRadius={5}
                  style={{
                    float: "left"
                  }}
                />
                <IAButton
                  textColor={this.props.store.highlightColor}
                  darkMode={this.props.store.darkMode}
                  label={this.localizationService.strings.PlayAdmin_DeletePopup_No?.toUpperCase()}
                  onClick={() => this.setState({ showConfirmClosePopup: false })}
                  style={{
                    float: "left",
                    marginLeft: 30
                  }}
                />
              </>
            }
          />
        }
        {this.state.selectedWidgetIndex != undefined && this.props.store.widgetToEdit == undefined &&
          <WidgetPopup
            selectedWidgetIndex={this.state.selectedWidgetIndex}
            availableWidgets={this.state.availableWidgets}
            setSelectedWidgetType={(selectedWidgetType: IPlayWidgetType) => this.getWidgetsOfType(selectedWidgetType)}
            close={() => this.setState({ selectedWidgetIndex: undefined, selectedWidgetType: undefined })}
          />
        }
        {this.props.store.slideToEdit ?
          <div
            className={styles.IA_slidesEditPanel}
            style={{
              height: window.innerHeight - 45,
              backgroundColor: this.props.store.darkMode ? "#414141" : "#777777",
              color: this.props.store.darkMode ? "#ffffff" : "#333333"
            }}
          >
            <div className={styles.IA_content} style={{ backgroundColor: this.props.store.darkMode ? Helper.darkModeBackgroundColor : '#fff' }} >
              <h1 style={{ color: this.props.store.darkMode ? "#ffffff" : '#333333' }}>{this.localizationService.strings.PlayAdmin_MenuArea_SlideSettings}</h1>
              <IATextField
                label={this.localizationService.strings.PlayAdmin_Title?.toUpperCase()}
                text={this.props.store.slideToEdit.title}
                required
                darkMode={this.props.store.darkMode}
                highlightColor={this.props.store.highlightColor}
                borderRadius={5}
                onChange={(title: string) => {
                  this.props.store.slideToEdit.title = title;
                  this.forceUpdate();
                }}
              />
              <IADateAndTimePicker
                label={this.localizationService.strings.Messages_EditPanel_StartTime?.toUpperCase()}
                selectedDate={new Date(this.props.store.slideToEdit.startDate)}
                showTimeSelector={true}
                highlightColor={this.props.store.highlightColor}
                isClearable={false}
                required={true}
                darkMode={this.props.store.darkMode}
                borderRadius={5}
                onChange={(startDate: Date) => {
                  this.props.store.slideToEdit.startDate = startDate.toJSON();
                }}
                style={{
                  marginTop: 20,
                  width: "100%"
                }}
              />
              <IADateAndTimePicker
                label={this.localizationService.strings.Messages_EditPanel_EndTime?.toUpperCase()}
                selectedDate={this.props.store.slideToEdit.endDate ? new Date(this.props.store.slideToEdit.endDate) : undefined}
                showTimeSelector={true}
                highlightColor={this.props.store.highlightColor}
                isClearable={true}
                darkMode={this.props.store.darkMode}
                borderRadius={5}
                onChange={(endDate: Date | undefined) => {
                  this.props.store.slideToEdit.endDate = endDate?.toJSON() ?? null;
                }}
                style={{
                  marginTop: 20,
                  width: "100%"
                }}
              />
              <IAOptionSelector
                label={this.localizationService.strings.PlayAdmin_SlideLayout?.toUpperCase()}
                options={[
                  {
                    icon: PlayHelper.getIconForSlideLayout("oneColumn"),
                    key: "oneColumn",
                    text: this.localizationService.strings.PlayAdmin_LayoutOneColumn,
                    disabled: this.props.store.slideToEdit.id != undefined && (this.props.store.slideToEdit.layout === "portraitOneRow" || this.props.store.slideToEdit.layout === "portraitTwoRows")
                  },
                  {
                    icon: PlayHelper.getIconForSlideLayout("twoColumns"),
                    key: "twoColumns",
                    text: this.localizationService.strings.PlayAdmin_LayoutTwoColumns,
                    disabled: this.props.store.slideToEdit.id != undefined && (this.props.store.slideToEdit.layout === "portraitOneRow" || this.props.store.slideToEdit.layout === "portraitTwoRows")
                  },
                  {
                    icon: PlayHelper.getIconForSlideLayout("rightSideTwoRows"),
                    key: "rightSideTwoRows",
                    text: this.localizationService.strings.PlayAdmin_LayoutRightSideTwoRows,
                    disabled: this.props.store.slideToEdit.id != undefined && (this.props.store.slideToEdit.layout === "portraitOneRow" || this.props.store.slideToEdit.layout === "portraitTwoRows")
                  },
                  {
                    icon: PlayHelper.getIconForSlideLayout("leftAndRightSideTwoRows"),
                    key: "leftAndRightSideTwoRows",
                    text: this.localizationService.strings.PlayAdmin_LayoutLeftAndRightSideTwoRows,
                    disabled: this.props.store.slideToEdit.id != undefined && (this.props.store.slideToEdit.layout === "portraitOneRow" || this.props.store.slideToEdit.layout === "portraitTwoRows")
                  },
                  {
                    icon: PlayHelper.getIconForSlideLayout("portraitOneRow"),
                    key: "portraitOneRow",
                    text: this.localizationService.strings.PlayAdmin_LayoutPortraitOneRow,
                    disabled: this.props.store.slideToEdit.id != undefined && (this.props.store.slideToEdit.layout === "oneColumn" || this.props.store.slideToEdit.layout === "twoColumns" || this.props.store.slideToEdit.layout === "rightSideTwoRows" || this.props.store.slideToEdit.layout === "leftAndRightSideTwoRows")
                  },
                  {
                    icon: PlayHelper.getIconForSlideLayout("portraitTwoRows"),
                    key: "portraitTwoRows",
                    text: this.localizationService.strings.PlayAdmin_LayoutPortraitTwoRows,
                    disabled: this.props.store.slideToEdit.id != undefined && (this.props.store.slideToEdit.layout === "oneColumn" || this.props.store.slideToEdit.layout === "twoColumns" || this.props.store.slideToEdit.layout === "rightSideTwoRows" || this.props.store.slideToEdit.layout === "leftAndRightSideTwoRows")
                  }
                ]}
                selected={this.props.store.slideToEdit.layout}
                highlightColor={this.props.store.highlightColor}
                list
                iconSize={30}
                darkMode={this.props.store.darkMode}
                borderRadius={5}
                onChange={(layout: IPlaySlideLayout) => {
                  const wasPortrait = this.props.store.slideToEdit.layout === "portraitOneRow" || this.props.store.slideToEdit.layout === "portraitTwoRows";
                  const isPortrait = layout === "portraitOneRow" || layout === "portraitTwoRows";
                  if (wasPortrait !== isPortrait) {
                    // Switching between horizontal/vertical shows a different set of playlists (filtered by screenFormat),
                    // so any pending (unsaved) selections made for the previous orientation no longer apply.
                    this.props.store.slideToEdit.playlistsToAddTo = [];
                    this.props.store.slideToEdit.playlistsToRemoveFrom = [];
                  }
                  this.props.store.slideToEdit.layout = layout;
                  const numberOfWidgetsForLayout = PlayHelper.getNumberOfWidgetsForLayout(layout);
                  const widgetsToSave = [];
                  if (this.props.store.slideToEdit?.widgets?.length > 0) {
                    this.props.store.slideToEdit.widgets.forEach((widget: IPlayWidgetExtended, index) => {
                      if (index < numberOfWidgetsForLayout) {
                        widgetsToSave.push(widget);
                      }
                    });
                    this.props.store.slideToEdit.widgets = widgetsToSave;
                  }
                  this.forceUpdate();
                  setTimeout(() => {
                    this.forceUpdate();
                  }, 0);
                }}
                style={{ clear: "none", marginTop: 20 }}
              />
              <IAToggle
                style={{
                  marginTop: 20,
                  width: "50%",
                  float: "left"
                }}
                label={this.localizationService.strings.PlayAdmin_ShowFooter?.toUpperCase()}
                checked={this.props.store.slideToEdit.showFooter}
                color={this.props.store.highlightColor}
                darkMode={this.props.store.darkMode}
                onChange={() => {
                  this.props.store.slideToEdit.showFooter = !this.props.store.slideToEdit.showFooter;
                }}
              />
              <div className={this.props.store.darkMode ? styles.IA_dividerDarkMode : styles.IA_divider} />
              <IAToggle
                style={{
                  marginTop: 20,
                  width: "50%",
                  float: "left",
                  clear: "both"
                }}
                label={this.localizationService.strings.PlayAdmin_ShowRssTicker?.toUpperCase()}
                checked={!!this.props.store.slideToEdit.showRssTicker}
                color={this.props.store.highlightColor}
                darkMode={this.props.store.darkMode}
                onChange={() => {
                  this.props.store.slideToEdit.showRssTicker = !!!this.props.store.slideToEdit.showRssTicker;
                  this.forceUpdate();
                }}
              />
              {/* RSS TICKER */}
              {this.state.selectedRssTickerPosition != undefined &&
                <div
                  style={{
                    clear: "both",
                    float: "left",
                    width: "100%"
                  }}
                >
                </div>
              }
              <div className={this.props.store.darkMode ? styles.IA_dividerDarkMode : styles.IA_divider} />
              {/* <h3 style={{ clear: "both", color: this.props.store.darkMode ? "#ffffff" : '#333333' }}>{this.localizationService.strings.PlayAdmin_AddSomeFun}</h3> */}
              {/* <IAToggle
                labelStyle={{ fontSize: "12px" }}
                label={this.localizationService.strings.PlayAdmin_ShowSnow?.toUpperCase()}
                checked={this.props.store.slideToEdit.showSnow}
                color={this.props.store.highlightColor}
                darkMode={this.props.store.darkMode}
                style={{ marginTop: 20 }}
                onChange={() => {
                  this.props.store.slideToEdit.showSnow = this.props.store.slideToEdit.showSnow != undefined ? !this.props.store.slideToEdit.showSnow : true;
                  this.forceUpdate();
                }}
              /> */}
              {/* <IAToggle
                labelStyle={{ fontSize: "12px" }}
                label={this.localizationService.strings.PlayAdmin_ShowConfetti?.toUpperCase()}
                checked={this.props.store.slideToEdit.showConfetti}
                color={this.props.store.highlightColor}
                darkMode={this.props.store.darkMode}
                style={{ marginTop: 20 }}
                onChange={() => {
                  this.props.store.slideToEdit.showConfetti = this.props.store.slideToEdit.showConfetti != undefined ? !this.props.store.slideToEdit.showConfetti : true;
                  this.forceUpdate();
                }}
              /> */}
              {/* <IAToggle
                labelStyle={{ fontSize: "12px" }}
                label={(this.localizationService.strings.Navigator_ImageConfetti || "Image confetti").toUpperCase()}
                checked={!!this.props.store.slideToEdit.showImageConfetti}
                color={this.props.store.highlightColor}
                darkMode={this.props.store.darkMode}
                style={{ marginTop: 20 }}
                onChange={() => {
                  this.props.store.slideToEdit.showImageConfetti = !this.props.store.slideToEdit.showImageConfetti;
                  if (this.props.store.slideToEdit.showImageConfetti && !this.props.store.slideToEdit.imageConfettiUrls?.length) {
                    this.props.store.slideToEdit.imageConfettiUrls = [...(imageConfettiTemplates[0]?.imageUrls || [])].filter((imageUrl) => !!imageUrl);
                  }
                  this.forceUpdate();
                }}
              /> */}
              {this.props.store.slideToEdit.showImageConfetti &&
                <>
                  <IAOptionSelector
                    label={this.localizationService.strings.Navigator_Admin_ImageConfettiTemplates || "Templates"}
                    selected={imageConfettiTemplates.find((template) => {
                      const imageUrls = this.props.store.slideToEdit.imageConfettiUrls || [];
                      const templateImageUrls = template.imageUrls.filter((imageUrl) => !!imageUrl);
                      return imageUrls.length === templateImageUrls.length && templateImageUrls.every((imageUrl) => imageUrls.indexOf(imageUrl) !== -1);
                    })?.key}
                    options={imageConfettiTemplates.map((template) => ({
                      key: template.key,
                      text: IMAGE_CONFETTI_TEMPLATE_LOCALIZATION_KEYS[template.key] ? this.localizationService.strings[IMAGE_CONFETTI_TEMPLATE_LOCALIZATION_KEYS[template.key].title] || template.title : template.title,
                      image: PlayHelper.getImageConfettiThumbnail(this.props.store.environment, this.props.store.tenantId, this.props.store.profile?.id, template.imageUrls.find((imageUrl) => !!imageUrl)),
                      width: 100
                    }))}
                    highlightColor={this.props.store.highlightColor}
                    darkMode={this.props.store.darkMode}
                    style={{ marginTop: 20, clear: "both", width: "calc(100% + 10px)" }}
                    onChange={(templateKey) => {
                      const template = imageConfettiTemplates.find((item) => item.key === templateKey);
                      if (template) {
                        this.props.store.slideToEdit.imageConfettiUrls = template.imageUrls.filter((imageUrl) => !!imageUrl);
                        this.forceUpdate();
                      }
                    }}
                  />
                  <IAMessageBar
                    type="information"
                    content={this.localizationService.strings.PlayAdmin_ImageConfettiTemplateInfo}
                    darkMode={this.props.store.darkMode}
                    style={{
                      marginTop: 12,
                      float: "left",
                      borderRadius: 5,
                    }}
                    onDismiss={() => { }}
                  />
                </>
              }
              {
                this.props.showPlaylistSelector && this.props.store.slideToEdit && <div className={`${styles.IA_playlistsSelector} ${this.props.store.darkMode ? styles.dark : ''}`}>
                  <h4>{this.localizationService.strings.PlayAdmin_PublishToPlaylist}</h4>
                  <div className={styles.IA_playlistsSelectAllRow}>
                    <div>
                      Select All
                    </div>
                    <IACheckbox
                      highlightColor={this.props.store.highlightColor}
                      isChecked={this.state.playlists?.every(playlist => (playlist?.slides.some(slideId => slideId === this.props.store.slideToEdit.id) || this.props.store.slideToEdit.playlistsToAddTo.some(playlistId => playlist.id === playlistId)) && this.props.store.slideToEdit.playlistsToRemoveFrom?.every(playlistId => playlistId !== playlist.id))}
                      onChange={(isChecked) => {
                        if (isChecked) {
                          this.state.playlists?.forEach(playlist => {
                            playlist?.slides?.every(slideId => slideId != this.props.store.slideToEdit.id) && this.props.store.slideToEdit.playlistsToAddTo.push(playlist.id);
                            this.props.store.slideToEdit.playlistsToRemoveFrom = [...this.props.store.slideToEdit.playlistsToRemoveFrom.filter(playlistId => playlistId !== playlist.id)];
                          })
                        } else {
                          this.state.playlists?.forEach(playlist => {
                            playlist?.slides?.some(slideId => slideId === this.props.store.slideToEdit.id) && this.props.store.slideToEdit.playlistsToRemoveFrom.push(playlist.id);
                            this.props.store.slideToEdit.playlistsToAddTo = [...this.props.store.slideToEdit.playlistsToAddTo.filter(playlistId => playlistId !== playlist.id)];
                          })
                        }
                      }}
                    />
                  </div>
                  {
                    this.state.playlists?.map((playlist) => {
                      if (
                        (playlist.screenFormat === "portrait" && this.props.store.slideToEdit.layout === "portraitOneRow" || this.props.store.slideToEdit.layout === "portraitTwoRows") ||
                        ((playlist.screenFormat == undefined || playlist.screenFormat === "landscape") && (this.props.store.slideToEdit.layout !== "portraitOneRow" && this.props.store.slideToEdit.layout !== "portraitTwoRows"))
                      ) {
                        return <div key={playlist.id} className={styles.IA_playlistsSelectorRow}>
                          <div>
                            {playlist.title}
                          </div>
                          <IACheckbox
                            highlightColor={this.props.store.highlightColor}
                            isChecked={(playlist?.slides.some(slideId => slideId === this.props.store.slideToEdit.id) || this.props.store.slideToEdit.playlistsToAddTo.some(playlistId => playlist.id === playlistId)) && this.props.store.slideToEdit.playlistsToRemoveFrom?.every(playlistId => playlistId !== playlist.id)}
                            onChange={(isChecked) => {
                              if (isChecked) {
                                playlist?.slides?.every(slideId => slideId != this.props.store.slideToEdit.id) && this.props.store.slideToEdit.playlistsToAddTo.push(playlist.id);
                                this.props.store.slideToEdit.playlistsToRemoveFrom = [...this.props.store.slideToEdit.playlistsToRemoveFrom.filter(playlistId => playlistId !== playlist.id)];
                              }
                              else {
                                playlist?.slides.some(slideId => slideId === this.props.store.slideToEdit.id) && this.props.store.slideToEdit.playlistsToRemoveFrom.push(playlist.id);
                                this.props.store.slideToEdit.playlistsToAddTo = [...this.props.store.slideToEdit.playlistsToAddTo.filter(playlistId => playlistId !== playlist.id)];
                              }
                            }}
                          />
                        </div>
                      }
                    })
                  }
                  {
                    this.state.isLoadingMore && <div>
                      <IASpinner color={this.props.store.highlightColor} />
                    </div>
                  }
                  {
                    this.state.playlists?.length < this.state.playlistsCount && <div className={styles.IA_playlistsSelectorLoadMore}>
                      <IAButton
                        label='Load more'
                        buttonColor={this.props.store.highlightColor}
                        onClick={() => this.loadPlaylists()}
                        darkMode={this.props.store.darkMode}
                      />
                    </div>
                  }

                </div>
              }
            </div>

            <div className={styles.IA_previewArea}>
              <div
                className={styles.IA_preview}
                style={{
                  width: this.props.store.isMobile ? "100%" : previewWidth,
                  height: previewHeight,
                  paddingLeft: this.props.store.slideToEdit?.layout === "portraitOneRow" || this.props.store.slideToEdit?.layout === "portraitTwoRows" ? `calc(50% - ${previewWidth / 2}px)` : 40
                }}
              >
                <div
                  className={styles.IA_previewScreen}
                  style={{
                    height: previewHeight,
                    width: previewWidth
                  }}
                >
                  {this.props.store.slideToEdit?.showRssTicker &&
                    <RssTicker
                      rssTicker={{}}
                      editMode={true}
                      isActive={true}
                      sizeUnit={(previewHeight > previewWidth ? previewWidth : previewHeight) / 100}
                      playerWidth={previewWidth}
                      headlineFont={this.props.store.profile?.fonts?.headline ? JSON.parse(this.props.store.profile?.fonts?.headline).name : '"Segoe UI Web (West European)", Segoe UI, -apple-system, BlinkMacSystemFont, Roboto, Helvetica Neue, sans-serif'}
                      contentFont={this.props.store.profile?.fonts?.body ? JSON.parse(this.props.store.profile?.fonts?.body).name : '"Segoe UI Web (West European)", Segoe UI, -apple-system, BlinkMacSystemFont, Roboto, Helvetica Neue, sans-serif'}
                      bottom={this.props.store.slideToEdit.showFooter ? ((previewHeight > previewWidth ? previewWidth : previewHeight) / 100) * 8 : 0}

                    />
                  }
                  {this.props.store.slideToEdit?.showFooter &&
                    <Footer
                      footer={this.props.store.profile?.footer}
                      sizeUnit={(previewHeight > previewWidth ? previewWidth : previewHeight) / 100}
                      city={this.props.store.city}
                      currentWeather={this.props.store.weatherData?.current}
                      editMode={true}
                    />
                  }
                  <Slide
                    index={0}
                    slide={this.props.store.slideToEdit}
                    isActive={true}
                    height={previewHeight}
                    width={previewWidth}
                    left={0}
                    footer={this.props.store.slideToEdit.showFooter ? this.props.store.profile.footer : undefined}
                    city={this.props.store.city}
                    editMode
                    highlightColor={this.props.store.highlightColor}
                    editWidgetIndex={(index: number) => this.setState({ selectedWidgetIndex: index, selectedRssTickerPosition: undefined })}
                    environment={this.props.store.environment}
                    tenantId={this.props.store.tenantId}
                    profileId={this.props.store.profile.id}
                    playerId='admin'
                    showActivityLabel
                    standalone
                    forecast={this.props.store.weatherData}
                    isHardcodedWeatherData={this.props.store.isUsingHardCodedWeather}
                    headlineFont={this.props.store.profile?.fonts?.headline ? JSON.parse(this.props.store.profile?.fonts?.headline).name : '"Segoe UI Web (West European)", Segoe UI, -apple-system, BlinkMacSystemFont, Roboto, Helvetica Neue, sans-serif'}
                    contentFont={this.props.store.profile?.fonts?.body ? JSON.parse(this.props.store.profile?.fonts?.body).name : '"Segoe UI Web (West European)", Segoe UI, -apple-system, BlinkMacSystemFont, Roboto, Helvetica Neue, sans-serif'}
                    nextSlide={() => { }}
                  />
                  {this.props.store.slideToEdit?.showSnow &&
                    <div
                      style={{
                        display: "block",
                        position: "absolute",
                        top: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 4000,
                        pointerEvents: "none"
                      }}
                    >
                      <Snowfall color="white" />
                    </div>
                  }
                  {this.props.store.slideToEdit?.showConfetti &&
                    <Confetti
                      width={previewWidth}
                      height={previewHeight}
                      gravity={0.05}
                      numberOfPieces={200}
                      style={{ zIndex: 4000 }}
                    />
                  }
                  {this.props.store.slideToEdit?.showImageConfetti && this.getImageConfettiUrls().length > 0 && this.state.imageConfettiReadyKey === JSON.stringify(this.getImageConfettiUrls()) &&
                    <Confetti
                      key={this.state.imageConfettiReadyKey}
                      width={previewWidth}
                      height={previewHeight}
                      gravity={0.02}
                      tweenDuration={1200}
                      tweenFunction={linearTween}
                      numberOfPieces={45}
                      confettiSource={{ x: 0, y: -20, w: previewWidth, h: 0 }}
                      drawShape={createImageConfettiDrawShape(this.getImageConfettiUrls(), this.resolveConfettiImageSrc)}
                      style={{ zIndex: 4000, pointerEvents: "none" }}
                    />
                  }
                </div>
              </div>
            </div>
          </div>
          :
          <div
            className={styles.IA_loading}
            style={{
              backgroundColor: this.props.store.darkMode ? "rgba(0, 0, 0, 0.5)" : "rgba(255, 255, 255, 0.5)"
            }}
          >
            <IASpinner
              key={Helper.getRandomStringKey()}
              color={this.props.store.highlightColor}
            />
            <div className={styles.IA_weAreWorkingOnIt}>{this.localizationService.strings.Composer_WeAreWorkingOnIt}</div>
          </div>
        }
        <WidgetEditPanel
          widgetId={undefined}
          close={() => {
            this.props.store.widgetToEdit = undefined;
          }}
          onSave={() => {
            this.props.store.workingOnIt = true;
            const widgetModel: IPlayWidget = {
              ...this.props.store.widgetToEdit, articleViewer: { articles: this.props.store.widgetToEdit.articleViewer?.articles?.map(article => article.id) }
            }
            PlayService.createWidget(this.props.store.environment, this.props.store.token, this.props.store.tenantId, this.props.store.profile?.id, widgetModel).then((id) => {
              console.log(`Created widget with id: ${id}`)
              this.props.store.widgetToEdit = undefined;
              PlayService.getWidgetById(this.props.store.environment, this.props.store.token, this.props.store.tenantId, this.props.store.profile?.id, id).then((widget) => {
                this.props.store.slideToEdit.widgets[this.state.selectedWidgetIndex] = widget;
                this.props.store.workingOnIt = false;
                this.setState({ selectedWidgetIndex: undefined });
              }).catch(e => {
                this.props.store.workingOnIt = false;
                this.props.store.generalErrorPopup = {
                  technicalDetails: e
                };
              });
            }).catch(e => {
              this.props.store.workingOnIt = false;
              this.props.store.generalErrorPopup = {
                technicalDetails: e
              };
            });
          }}
          availableTypes={PlayHelper.getAvailableWidgets(this.localizationService, this.props.store.environment, this.props.store.isConnectedToMessages(), this.props.store.isConnectedToEvents(), this.props.store.isConnectedToSharepointNews(), this.props.store.isConnectedToPowerBi())}
          preSelectedType={this.state.selectedWidgetType}
        />
      </IAPanel >
    );
  }


}